// ─── 颜色工具函数 ────────────────────────────────────────────────────────────────

// ─── 颜色空间转换 ─────────────────────────────────────────────────────────────

export function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  const len = clean.length;
  let r, g, b, a = 255;
  if (len === 3 || len === 4) {
    r = parseInt(clean[0] + clean[0], 16);
    g = parseInt(clean[1] + clean[1], 16);
    b = parseInt(clean[2] + clean[2], 16);
    if (len === 4) a = parseInt(clean[3] + clean[3], 16);
  } else if (len === 6 || len === 8) {
    r = parseInt(clean.slice(0, 2), 16);
    g = parseInt(clean.slice(2, 4), 16);
    b = parseInt(clean.slice(4, 6), 16);
    if (len === 8) a = parseInt(clean.slice(6, 8), 16);
  } else throw new Error('无效的 HEX 颜色');
  return { r, g, b, a };
}

export function rgbToHex({ r, g, b, a = 255 }) {
  const toHex = n => Math.round(n).toString(16).padStart(2, '0');
  return '#' + toHex(r) + toHex(g) + toHex(b) + (a < 255 ? toHex(a) : '');
}

export function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function hslToRgb({ h, s, l }) {
  s /= 100; l /= 100;
  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return { r: Math.round(f(0) * 255), g: Math.round(f(8) * 255), b: Math.round(f(4) * 255) };
}

export function rgbToCmyk({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const k = 1 - Math.max(r, g, b);
  if (k === 1) return { c: 0, m: 0, y: 0, k: 100 };
  const c = (1 - r - k) / (1 - k);
  const m = (1 - g - k) / (1 - k);
  const y = (1 - b - k) / (1 - k);
  return { c: Math.round(c * 100), m: Math.round(m * 100), y: Math.round(y * 100), k: Math.round(k * 100) };
}

export function cmykToRgb({ c, m, y, k }) {
  c /= 100; m /= 100; y /= 100; k /= 100;
  return {
    r: Math.round(255 * (1 - c) * (1 - k)),
    g: Math.round(255 * (1 - m) * (1 - k)),
    b: Math.round(255 * (1 - y) * (1 - k)),
  };
}

export function rgbToHsv({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const v = max, d = max - min;
  const s = max === 0 ? 0 : d / max;
  let h = 0;
  if (max !== min) {
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), v: Math.round(v * 100) };
}

// ─── 全格式转换 ───────────────────────────────────────────────────────────────

export function convertColor(input) {
  let rgb;
  const s = input.trim();

  // Detect format
  if (s.startsWith('#')) {
    rgb = hexToRgb(s);
  } else if (s.startsWith('rgb')) {
    const m = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/);
    if (!m) throw new Error('无效的 RGB 格式');
    rgb = { r: +m[1], g: +m[2], b: +m[3], a: m[4] !== undefined ? Math.round(+m[4] * 255) : 255 };
  } else if (s.startsWith('hsl')) {
    const m = s.match(/hsla?\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/);
    if (!m) throw new Error('无效的 HSL 格式');
    rgb = hslToRgb({ h: +m[1], s: +m[2], l: +m[3] });
    rgb.a = 255;
  } else if (s.includes(',') && s.split(',').length === 3) {
    const parts = s.split(',').map(Number);
    rgb = { r: parts[0], g: parts[1], b: parts[2], a: 255 };
  } else {
    throw new Error('支持 HEX、RGB(A)、HSL、R,G,B 格式');
  }

  const hsl = rgbToHsl(rgb);
  const hsv = rgbToHsv(rgb);
  const cmyk = rgbToCmyk(rgb);
  const hex = rgbToHex(rgb);
  const alpha = (rgb.a ?? 255) / 255;

  return {
    hex: hex.toUpperCase(),
    rgb: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
    rgba: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha.toFixed(2)})`,
    hsl: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`,
    hsv: `hsv(${hsv.h}, ${hsv.s}%, ${hsv.v}%)`,
    cmyk: `cmyk(${cmyk.c}%, ${cmyk.m}%, ${cmyk.y}%, ${cmyk.k}%)`,
    raw: rgb,
  };
}

// ─── 颜色对比检查（WCAG） ─────────────────────────────────────────────────────

function relativeLuminance({ r, g, b }) {
  const sRGB = [r, g, b].map(c => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];
}

export function contrastCheck(color1, color2) {
  const rgb1 = hexToRgb(color1.startsWith('#') ? color1 : '#' + color1);
  const rgb2 = hexToRgb(color2.startsWith('#') ? color2 : '#' + color2);
  const L1 = relativeLuminance(rgb1);
  const L2 = relativeLuminance(rgb2);
  const ratio = (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
  const r = Math.round(ratio * 100) / 100;
  return {
    ratio: r,
    ratioStr: `${r}:1`,
    aa_normal: r >= 4.5,
    aa_large: r >= 3,
    aaa_normal: r >= 7,
    aaa_large: r >= 4.5,
    wcag: r >= 7 ? 'AAA' : r >= 4.5 ? 'AA' : r >= 3 ? 'AA (大文字)' : '不符合 WCAG',
  };
}

// ─── 颜色生成器（调色板） ──────────────────────────────────────────────────────

export function generatePalette(baseHex, mode = 'analogous') {
  const rgb = hexToRgb(baseHex);
  const hsl = rgbToHsl(rgb);
  const shades = [];

  switch (mode) {
    case 'analogous':
      for (let i = -2; i <= 2; i++) {
        const h = (hsl.h + i * 30 + 360) % 360;
        shades.push(rgbToHex({ ...hslToRgb({ h, s: hsl.s, l: hsl.l }), a: 255 }));
      }
      break;
    case 'complementary':
      shades.push(baseHex);
      shades.push(rgbToHex({ ...hslToRgb({ h: (hsl.h + 180) % 360, s: hsl.s, l: hsl.l }), a: 255 }));
      break;
    case 'triadic':
      for (let i = 0; i < 3; i++) {
        const h = (hsl.h + i * 120) % 360;
        shades.push(rgbToHex({ ...hslToRgb({ h, s: hsl.s, l: hsl.l }), a: 255 }));
      }
      break;
    case 'shades':
      for (let l = 10; l <= 90; l += 10) {
        shades.push(rgbToHex({ ...hslToRgb({ h: hsl.h, s: hsl.s, l }), a: 255 }));
      }
      break;
    case 'monochromatic':
      for (let s = 20; s <= 100; s += 20) {
        shades.push(rgbToHex({ ...hslToRgb({ h: hsl.h, s, l: hsl.l }), a: 255 }));
      }
      break;
    case 'tetradic':
      for (let i = 0; i < 4; i++) {
        const h = (hsl.h + i * 90) % 360;
        shades.push(rgbToHex({ ...hslToRgb({ h, s: hsl.s, l: hsl.l }), a: 255 }));
      }
      break;
    default:
      shades.push(baseHex);
  }
  return shades;
}

// ─── 从图片提取颜色（canvas） ─────────────────────────────────────────────────

export async function extractColorsFromImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const size = 50;
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, size, size);
      const data = ctx.getImageData(0, 0, size, size).data;
      URL.revokeObjectURL(url);

      const colorMap = {};
      for (let i = 0; i < data.length; i += 4 * 3) {
        const r = Math.round(data[i] / 32) * 32;
        const g = Math.round(data[i + 1] / 32) * 32;
        const b = Math.round(data[i + 2] / 32) * 32;
        const key = `${r},${g},${b}`;
        colorMap[key] = (colorMap[key] || 0) + 1;
      }
      const sorted = Object.entries(colorMap).sort((a, b) => b[1] - a[1]).slice(0, 10);
      const colors = sorted.map(([key]) => {
        const [r, g, b] = key.split(',').map(Number);
        return rgbToHex({ r, g, b, a: 255 });
      });
      resolve(colors);
    };
    img.onerror = reject;
    img.src = url;
  });
}
